import { useEffect, useState } from "react";
import SimpleAnimation from "../../components/SimpleAnimation";
import "./Home.css";
import { Link } from "react-router-dom";
import { fetchMultipleNpmStats } from "../../services/activityService";

const STATUS_MAP = {
  Aktywny: { cls: "badge--active", label: "Active" },
  "W Rozwoju": { cls: "badge--dev", label: "In Development" },
  Ukończony: { cls: "badge--done", label: "Completed" },
  Różne: { cls: "badge--done", label: "Various" },
};

const Home = ({
  projects = [],
  news = [],
  authors = [],
  achievements = {},
  roadmap = [],
  perfMode = false,
}) => {
  const [npmStats, setNpmStats] = useState([]);
  const [projectExpanded, setProjectExpanded] = useState(false);
  const [activeProject, setActiveProject] = useState(0);

  const totalNpmDownloads =
    npmStats.length > 0
      ? npmStats
          .reduce((total, pkg) => total + pkg.totalDownloads, 0)
          .toLocaleString()
      : "0";

  const featuredProjects = (
    projects.filter((p) => p.featured).slice(0, 5).length
      ? projects.filter((p) => p.featured)
      : projects
  ).slice(0, 5);

  const gameProjects = projects.filter((p) => p.category === "Gry");
  const modelProjects = projects.filter((p) => p.category === "Assety 3D");
  const webProjects = projects.filter((p) =>
    ["Web", "Narzędzia", "Assety 3D"].includes(p.category),
  );

  const makeStripTiles = (arr) => {
    if (!arr.length) return [];
    const base =
      arr.length < 6
        ? Array.from({ length: Math.ceil(12 / arr.length) }, () => arr).flat()
        : arr;
    return [...base, ...base];
  };

  const gameTiles = perfMode
    ? gameProjects.slice(0, 5)
    : makeStripTiles(gameProjects);
  const webTiles = perfMode
    ? webProjects.slice(0, 5)
    : makeStripTiles(webProjects);

  const latestNews = news.slice(0, 3);
  const upcomingRoadmap = roadmap.slice(0, 10);
  const author = authors[0] || {};

  useEffect(() => {
    fetchMultipleNpmStats()
      .then(setNpmStats)
      .catch((error) => console.error("Error fetching NPM stats:", error));
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) =>
        entries.forEach(
          (e) => e.isIntersecting && e.target.classList.add("animate-in"),
        ),
      { threshold: 0.1, rootMargin: "0px 0px -50px 0px" },
    );
    document
      .querySelectorAll(".animate-on-scroll")
      .forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <>
      {!perfMode && <SimpleAnimation />}

      <div className="home">
        {/* HERO */}
        <section className="hero" style={{ paddingTop: "60px" }}>
          <div className="hero-left">
            <div className="hero-eyebrow">
              {/* <div className="hero-eyebrow-dot" /> */}
              {/* <span>Independent developer · Solo · End-to-end</span> */}
            </div>
            <h1 className="hero-title">
              <div className="line-white">GAMES.</div>
              <div className="line-white">TOOLS.</div>
              <div className="line-acid">WEB.</div>
            </h1>
            <p className="hero-sub">
              Building <strong>games with Unity &amp; C#</strong>, open-source{" "}
              <strong>React libraries</strong>, 3D assets in{" "}
              <strong>Blender</strong>, and full-stack web products — solo, from
              concept to deployment.
            </p>
            <div className="hero-actions">
              <Link to="/projects" className="btn-primary">
                View Projects →
              </Link>
              <Link to="/profiles" className="btn-ghost">
                About me
              </Link>
            </div>
            <div className="hero-stats">
              <div className="stat">
                <div className="stat-num">{totalNpmDownloads}</div>
                <div className="stat-label">NPM Downloads</div>
              </div>
              <div className="stat">
                <div className="stat-num">{projects.length || "7"}+</div>
                <div className="stat-label">Projects</div>
              </div>
              <div className="stat">
                <div className="stat-num">50+</div>
                <div className="stat-label">Components</div>
              </div>
              <div className="stat">
                <div className="stat-num">2022</div>
                <div className="stat-label">Since</div>
              </div>
            </div>
          </div>
          <div className="hero-right">
            <Link to="/projects" className="hero-discipline">
              {gameTiles.length > 0 && (
                <div className="disc-bg-strip" aria-hidden="true">
                  <div
                    className={`disc-bg-track${perfMode ? " disc-bg-track--static" : ""}`}
                  >
                    {gameTiles.map((p, i) => (
                      <div key={i} className="disc-bg-tile">
                        <span className="disc-bg-tile-cat">{p.category}</span>
                        <span className="disc-bg-tile-name">{p.title}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <div className="disc-top">
                <div className="disc-num">01 / GAME DEV</div>
                <div className="disc-title">UNITY &amp; C#</div>
              </div>
              <div className="disc-bottom">
                <div className="disc-tags">
                  <span>Unity</span>
                  <span>Physics</span>
                  <span>AI</span>
                  <span>Multiplayer</span>
                </div>
                <div className="disc-arrow">→</div>
              </div>
            </Link>
            <Link to="/projects" className="hero-discipline">
              {webTiles.length > 0 && (
                <div
                  className="disc-bg-strip disc-bg-strip--rev"
                  aria-hidden="true"
                >
                  <div
                    className={`disc-bg-track${perfMode ? " disc-bg-track--static" : ""}`}
                  >
                    {webTiles.map((p, i) => (
                      <div key={i} className="disc-bg-tile">
                        <span className="disc-bg-tile-cat">{p.category}</span>
                        <span className="disc-bg-tile-name">{p.title}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <div className="disc-top">
                <div className="disc-num">02 / WEB + 3D</div>
                <div className="disc-title">REACT &amp; BLENDER</div>
              </div>
              <div className="disc-bottom">
                <div className="disc-tags">
                  <span>React</span>
                  <span>TypeScript</span>
                  <span>Blender</span>
                  <span>NPM</span>
                </div>
                <div className="disc-arrow">→</div>
              </div>
            </Link>
          </div>
        </section>

        {/* FEATURED PROJECTS — editorial 3×2 grid */}
        <section className="fp-section" id="projects">

          {/* ═══ ROW 1: header ═══════════════════════════════════════ */}

          {/* H1 — section title */}
          <div className="fp-cell fp-cell--title">
            <div className="home-section-label">Portfolio</div>
            <h2 className="home-section-title">
              FEATURED
              <br />
              PROJECTS
            </h2>
          </div>

          {/* H2 — anim-target: 3D topo animation shows through this */}
          <div className="fp-cell fp-cell--anim anim-target" />

          {/* H3 — 3D assets header */}
          <div className="fp-cell fp-cell--3d-header">
            <div className="home-section-label">Blender</div>
            <h2 className="fp-models__title">
              3D
              <br />
              ASSETS
            </h2>
          </div>

          {/* ═══ ROW 2: content ══════════════════════════════════════ */}

          {/* C1 — project list */}
          <div className="fp-list">
            {featuredProjects.slice(0, 4).map((p, i) => {
              const st = STATUS_MAP[p.status] || { cls: "badge--done", label: p.status };
              const isActive = i === activeProject;
              return (
                <div
                  key={p.id}
                  className={`fp-list__item${isActive ? " is-active" : ""}`}
                  onClick={() => setActiveProject(i)}
                >
                  <span className="fp-list__idx">0{i + 1}</span>
                  <div className="fp-list__body">
                    <div className="fp-list__row1">
                      <span className="fp-list__title">{p.title}</span>
                      <span className={`home-badge home-badge--${st.cls.replace("badge--", "")}`}>
                        {st.label.toUpperCase()}
                      </span>
                    </div>
                    <span className="fp-list__cat">{p.category?.toUpperCase()}</span>
                    {p.description && (
                      <p className="fp-list__excerpt">{p.description}</p>
                    )}
                  </div>
                  <span className="fp-list__arrow">›</span>
                </div>
              );
            })}
          </div>

          {/* C2 — project detail, key triggers fade-in */}
          {(() => {
            const p = featuredProjects[activeProject];
            if (!p) return <div className="fp-detail" />;
            const st = STATUS_MAP[p.status] || { cls: "badge--done", label: p.status };
            const isLive = p.status === "W Rozwoju" || p.status === "Aktywny";
            return (
              <div className="fp-detail" key={activeProject}>
                <div className="fp-detail__img">
                  <img
                    src={`https://picsum.photos/seed/${p.id}/960/380`}
                    alt={p.title}
                  />
                  <div className="fp-detail__img-overlay" />
                  <div className="fp-detail__img-label">
                    <span className="fp-detail__img-num">0{activeProject + 1}</span>
                    <span className="fp-detail__img-title">{p.title?.toUpperCase()}</span>
                  </div>
                </div>

                <div className="fp-detail__body">
                  <div className="fp-detail__bg-num">0{activeProject + 1}</div>

                  <div className="fp-detail__top">
                    <div className="fp-detail__meta-row">
                      <span className="fp-detail__cat">{p.category?.toUpperCase()}</span>
                      <span className="fp-detail__dot-sep" />
                      <span className={`home-badge home-badge--${st.cls.replace("badge--", "")}`}>
                        {st.label.toUpperCase()}
                      </span>
                      {isLive && (
                        <span className="fp-detail__live">
                          <span className="fp-detail__live-dot" />
                          ACTIVE
                        </span>
                      )}
                    </div>

                    <h3 className="fp-detail__title">{p.title?.toUpperCase()}</h3>

                    {p.description && (
                      <p className="fp-detail__desc">{p.description}</p>
                    )}

                    {p.technologies?.length > 0 && (
                      <div className="fp-detail__stack">
                        {p.technologies.slice(0, 10).map((t) => (
                          <span key={t} className="home-stack-tag">{t}</span>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="fp-detail__footer">
                    <Link to={`/projects/${p.id}`} className="fp-detail__cta">
                      View full project →
                    </Link>
                    <span className="fp-detail__counter">
                      {activeProject + 1} / {Math.min(featuredProjects.length, 4)}
                    </span>
                  </div>
                </div>
              </div>
            );
          })()}

          {/* C3 — 3D models list */}
          <div className="fp-section__models">
            <div className="fp-models__list">
              {modelProjects.length > 0 ? modelProjects.map((p, i) => (
                <Link key={p.id} to={`/projects/${p.id}`} className="fp-models__item">
                  <span className="fp-models__item-idx">0{i + 1}</span>
                  <div className="fp-models__item-body">
                    <span className="fp-models__item-name">{p.title}</span>
                    {p.technologies?.length > 0 && (
                      <span className="fp-models__item-tech">{p.technologies.slice(0, 3).join(" · ")}</span>
                    )}
                  </div>
                  <span className="fp-models__item-arrow">›</span>
                </Link>
              )) : (
                <p className="fp-models__empty">No 3D assets yet.</p>
              )}
            </div>
            <Link to="/projects" className="fp-models__footer-link">
              View all assets →
            </Link>
          </div>

        </section>

        {/* Activity Hub */}
        {/* <section className="activity-hub-section">
          <div className="container-home">
            <div className="section-header">
              <h2>Centrum Aktywności</h2>
              <p>
                Najnowsze artykuły i aktualności z mojego świata developmentu
              </p>
            </div>
            <div className="hub-layout animate-on-scroll">
              <div className="news-main-section">
                <div className="news-header">
                  <div className="news-title">
                    <h3>Najnowsze Artykuły</h3>
                    <span className="news-count">
                      {
                        (activeSearchQuery
                          ? searchResults
                          : news.filter((article) => {
                              const matchesFilter =
                                selectedNewsFilter === "all" ||
                                article.articleType === selectedNewsFilter;
                              const matchesProject =
                                !selectedProject ||
                                article.projectId === selectedProject;
                              return matchesFilter && matchesProject;
                            })
                        ).length
                      }{" "}
                      artykułów
                    </span>
                  </div>
                  <div className="news-search-filters">
                    <div className="news-search-filter-dropdown-suggestion-wrapper">
                      {isSearchDropdownOpen && (
                        <div className="search-dropdown">
                          {getSearchSuggestions().map((article) => (
                            <div
                              key={article.id}
                              className="search-suggestion"
                              onClick={() => {
                                setSearchQuery(article.title);
                                setIsSearchDropdownOpen(false);
                                performSearch();
                              }}
                            >
                              <div className="suggestion-title">
                                {article.title}
                              </div>
                              <div className="suggestion-category">
                                {article.category?.join(", ")} •{" "}
                                {article.articleType}
                              </div>
                            </div>
                          ))}
                          {getSearchSuggestions().length === 0 && (
                            <div className="no-suggestions">
                              Brak wyników dla "{searchQuery}"
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                    <div
                      className="news-search-filter-dropdown-wrapper"
                      ref={dropdownRef}
                    >
                      {isProjectDropdownOpen && (
                        <div className="dropdown-menu">
                          <div
                            className={`dropdown-item ${!selectedProject ? "selected" : ""}`}
                            onClick={() => {
                              setSelectedProject("");
                              setIsProjectDropdownOpen(false);
                            }}
                          >
                            Wszystkie projekty
                          </div>
                          {projects.map((project) => (
                            <div
                              key={project.id}
                              className={`dropdown-item ${selectedProject === project.id ? "selected" : ""}`}
                              onClick={() => {
                                setSelectedProject(project.id);
                                setIsProjectDropdownOpen(false);
                              }}
                            >
                              {project.title}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="search-input-border-clip">
                      <div className="news-search-wrapper-m">
                        <div
                          className="search-container"
                          ref={searchDropdownRef}
                        >
                          <div className="search-input-wrapper">
                            <svg
                              className="search-icon"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                            >
                              <circle cx="11" cy="11" r="8" />
                              <path d="m21 21-4.35-4.35" />
                            </svg>
                            <input
                              type="text"
                              className="search-input"
                              placeholder="Szukaj w artykułach..."
                              value={searchQuery}
                              onChange={(e) => {
                                setSearchQuery(e.target.value);
                                const norm = normalizeText(e.target.value);
                                setIsSearchDropdownOpen(
                                  norm.length >= 2 ||
                                    lettersOnly(norm).length >= 2,
                                );
                              }}
                              onFocus={() => {
                                const norm = normalizeText(searchQuery);
                                if (
                                  norm.length >= 2 ||
                                  lettersOnly(norm).length >= 2
                                )
                                  setIsSearchDropdownOpen(true);
                              }}
                              onKeyPress={(e) => {
                                if (e.key === "Enter") performSearch();
                              }}
                            />
                          </div>
                        </div>
                        <div className="project-filter">
                          <div className="custom-dropdown">
                            <div
                              className="dropdown-trigger"
                              onClick={() =>
                                setIsProjectDropdownOpen(!isProjectDropdownOpen)
                              }
                            >
                              <span className="dropdown-text">
                                {selectedProject
                                  ? projects.find(
                                      (p) => p.id === selectedProject,
                                    )?.title
                                  : "Wszystkie projekty"}
                              </span>
                              <span
                                className={`dropdown-arrow ${isProjectDropdownOpen ? "open" : ""}`}
                              >
                                ▼
                              </span>
                            </div>
                          </div>
                        </div>
                        <button
                          className="search-button"
                          onClick={performSearch}
                          type="button"
                        >
                          Search
                        </button>
                      </div>
                    </div>
                    <div className="news-filters">
                      {filterButtons.map((filter) => (
                        <FilterButton
                          key={filter.value}
                          value={filter.value}
                          label={filter.label}
                          isActive={selectedNewsFilter === filter.value}
                          onClick={setSelectedNewsFilter}
                          wrapperClassName="search-input-border-clip"
                        />
                      ))}
                    </div>
                  </div>
                </div>

                {activeSearchQuery && (
                  <div className="search-results-header">
                    <div className="search-input-border-clip">
                      <div className="sort-controls">
                        <button
                          className={`sort-btn ${sortOrder === "newest" ? "active" : ""}`}
                          onClick={() => {
                            setSortOrder("newest");
                            if (activeSearchQuery) performSearch();
                          }}
                        >
                          Najnowsze
                        </button>
                        <button
                          className={`sort-btn ${sortOrder === "oldest" ? "active" : ""}`}
                          onClick={() => {
                            setSortOrder("oldest");
                            if (activeSearchQuery) performSearch();
                          }}
                        >
                          Najstarsze
                        </button>
                      </div>
                    </div>
                    <div className="active-search-info">
                      <span className="search-results-text">
                        Wyniki wyszukiwania dla:{" "}
                        <strong>"{activeSearchQuery}"</strong> (
                        {searchResults.length}{" "}
                        {searchResults.length === 1
                          ? "artykuł"
                          : searchResults.length < 5
                            ? "artykuły"
                            : "artykułów"}
                        )
                      </span>
                      <button
                        className="clear-search-btn"
                        onClick={clearSearch}
                        title="Wyczyść wyszukiwanie"
                      >
                        ✕
                      </button>
                    </div>
                  </div>
                )}

                <div className="news-bento-grid">
                  {(activeSearchQuery
                    ? searchResults
                    : news.filter((article) => {
                        const matchesFilter =
                          selectedNewsFilter === "all" ||
                          article.articleType === selectedNewsFilter;
                        const matchesProject =
                          !selectedProject ||
                          article.projectId === selectedProject;
                        return matchesFilter && matchesProject;
                      })
                  )
                    .slice(0, newsToShow)
                    .map((article, index) => (
                      <ArticleCard
                        key={article.id}
                        article={article}
                        index={index}
                        authors={authors}
                      />
                    ))}
                </div>

                {news.filter(
                  (article) =>
                    selectedNewsFilter === "all" ||
                    article.articleType === selectedNewsFilter,
                ).length > newsToShow && (
                  <div className="news-load-more">
                    <button
                      className="load-more-btn"
                      onClick={() => setNewsToShow((prev) => prev + 3)}
                    >
                      Pokaż więcej artykułów
                    </button>
                  </div>
                )}
              </div>

              <div className="activity-sidebar">
                <div className="clip-container">
                  <div className="recent-activity-compact">
                    <h4>Ostatnia Aktywność</h4>
                    <div className="activity-list">
                      {isLoadingActivities ? (
                        <div className="activity-loading">
                          <div className="loading-spinner" />
                          <span>Ładowanie aktywności...</span>
                        </div>
                      ) : activitiesError ? (
                        <div className="activity-error">
                          <span>⚠️ {activitiesError}</span>
                          <small>Wyświetlane są przykładowe dane</small>
                        </div>
                      ) : null}
                      {recentActivities.map((activity, index) => (
                        <ActivityItem
                          key={index}
                          type={activity.type}
                          title={activity.title}
                          desc={activity.desc}
                          linkTo={activity.linkTo}
                          embedUrl={activity.embedUrl}
                        />
                      ))}
                    </div>
                  </div>
                </div>

                <div className="featured-news">
                  <h4>Wyróżnione</h4>
                  <div className="featured-news-list">
                    {news
                      .filter((article) => article.featured)
                      .slice(0, 3)
                      .map((article) => (
                        <FeaturedNewsItem
                          key={article.id}
                          article={article}
                          authors={authors}
                          linkTo={`/article/${article.id}/${article.title
                            .slice(0, -1)
                            .replace(/[\s:]+/g, "-")
                            .toLowerCase()}`}
                        />
                      ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section> */}

        {/* What I Build */}
        {/* <section className="section section--alt">
          <div className="container">
            <div className="sec-header">
              <div className="sec-eyebrow">Expertise</div>
              <h2 className="sec-title">WHAT I BUILD</h2>
              <p className="sec-sub">
                Spanning games, web applications and 3D art — each discipline
                informs and sharpens the others.
              </p>
            </div>
            <div className="expertise-grid">
              {WWD.map((item, i) => (
                <div key={item.id} className="expertise-card">
                  <div className="expertise-num">0{i + 1}</div>
                  <div className="expertise-icon">
                    {ICONS[item.name] || "⚡"}
                  </div>
                  <div className="expertise-title">
                    {item.name?.toUpperCase()}
                  </div>
                  <p className="expertise-desc">{item.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section> */}

        {/* Latest News */}
        <section className="section section--alt">
          <div className="container">
            <div className="sec-header">
              <div className="sec-eyebrow">Updates</div>
              <h2 className="sec-title">LATEST NEWS</h2>
              <p className="sec-sub">
                Dev logs, releases, and updates straight from active projects.
              </p>
            </div>
            {/* Featured Article Hero */}
            {news[0] &&
              (() => {
                const article = news[0];
                const linkedProject = projects.find(
                  (p) => p.id === article.projectId,
                );
                const articleAuthor =
                  authors.find((a) => a.id === article.authorId) || author;
                const npmAch = achievements?.npm;
                const nextMilestone = npmAch?.milestones?.find(
                  (m) => !m.achieved,
                );
                const npmTarget = nextMilestone?.value || 1000;
                const npmTotal =
                  npmStats.length > 0
                    ? npmStats.reduce((s, p) => s + p.totalDownloads, 0)
                    : npmAch?.current || 0;
                const npmPct = Math.min(
                  Math.round((npmTotal / npmTarget) * 100),
                  100,
                );

                // Compute 8-week trend from daily stats
                const allDaily = npmStats.flatMap((p) => p.dailyStats || []);
                const weeklyTotals = (() => {
                  if (!allDaily.length) return Array(8).fill(0);
                  const sorted = [...allDaily].sort((a, b) =>
                    a.day > b.day ? 1 : -1,
                  );
                  const weeks = [];
                  for (let i = 0; i < 8; i++) {
                    const chunk = sorted.slice(
                      Math.max(0, sorted.length - 56 + i * 7),
                      Math.max(0, sorted.length - 56 + i * 7 + 7),
                    );
                    weeks.push(chunk.reduce((s, d) => s + d.downloads, 0));
                  }
                  return weeks;
                })();
                const maxWeek = Math.max(...weeklyTotals, 1);

                return (
                  <section className="section news-hero-section">
                    <div className="container">
                      <div className={`news-hub-grid${projectExpanded ? " project-expanded" : ""}`}>
                        {/* Left: Article */}
                        <Link
                          to={`/article/${article.id}/${article.title
                            .slice(0, -1)
                            .replace(/[\s:]+/g, "-")
                            .toLowerCase()}`}
                          className="news-hub-article"
                          tabIndex={projectExpanded ? -1 : 0}
                        >
                          <div className="news-hub-article-top">
                            <span className="news-hub-type">
                              {article.articleType?.toUpperCase()}
                            </span>
                            <div className="news-hub-meta">
                              <span className="news-hub-author">
                                {articleAuthor.nickname}
                              </span>
                              <span className="news-hub-sep">·</span>
                              <span className="news-hub-date">
                                {article.date}
                              </span>
                              {article.category?.slice(0, 2).map((c) => (
                                <span key={c} className="tag">
                                  {c}
                                </span>
                              ))}
                            </div>
                            <h2 className="news-hub-title">
                              {article.title?.toUpperCase()}
                            </h2>
                            <p className="news-hub-excerpt">
                              {article.content?.substring(0, 200)}...
                            </p>
                          </div>
                          {article.img && (
                            <div className="news-hub-img">
                              <img
                                src={`/images/${article.img}`}
                                alt={article.title}
                              />
                            </div>
                          )}
                          <span className="news-hub-cta">Czytaj artykuł →</span>
                        </Link>

                        {/* Right: Sidebar with 2 panels */}
                        <div className="news-hub-sidebar">
                          {/* Project panel */}
                          {linkedProject && (
                            <div
                              className={`news-hub-panel news-hub-panel--project${projectExpanded ? " is-expanded" : ""}`}
                              onClick={() => setProjectExpanded(p => !p)}
                              role="button"
                              aria-expanded={projectExpanded}
                            >
                              <div className="news-hub-proj-bg-overlay" />
                              <div className="news-hub-proj-content">
                                <div className="news-hub-panel-label">
                                  {projectExpanded ? (
                                    <button
                                      className="news-hub-collapse-btn"
                                      onClick={e => { e.stopPropagation(); setProjectExpanded(false); }}
                                    >
                                      ← ZAMKNIJ
                                    </button>
                                  ) : "AKTUALNIE"}
                                </div>
                                <div className="news-hub-panel-dot" />
                                <div className="news-hub-project-name">
                                  {linkedProject.title?.toUpperCase()}
                                </div>
                                <div className="news-hub-project-cat">
                                  {linkedProject.category?.toUpperCase()}
                                </div>
                                <div className="news-hub-status">
                                  <span className="news-hub-status-dot" />
                                  <span>
                                    {linkedProject.status?.toUpperCase()}
                                  </span>
                                </div>
                                <div className="news-hub-stack-label">STACK</div>
                                <div className="news-hub-stack">
                                  {linkedProject.technologies
                                    ?.slice(0, projectExpanded ? 12 : 4)
                                    .map((t) => (
                                      <span key={t} className="tag">
                                        {t.toUpperCase()}
                                      </span>
                                    ))}
                                </div>

                                {/* Extra content visible only when expanded */}
                                {projectExpanded && (linkedProject.description || linkedProject.desc) && (
                                  <p className="news-hub-proj-desc">
                                    {linkedProject.description || linkedProject.desc}
                                  </p>
                                )}

                                <div className="news-hub-panel-footer">
                                  {projectExpanded ? (
                                    <Link
                                      to={`/projects/${linkedProject.id}`}
                                      className="news-hub-milestone-btn"
                                      onClick={e => e.stopPropagation()}
                                    >
                                      ZOBACZ PROJEKT →
                                    </Link>
                                  ) : (
                                    <>
                                      <span className="news-hub-milestone-btn">
                                        MILESTONE
                                      </span>
                                      <span className="news-hub-see-more">
                                        ROZWIŃ →
                                      </span>
                                    </>
                                  )}
                                </div>
                              </div>
                            </div>
                          )}

                          {/* NPM Downloads panel */}
                          <div className={`news-hub-panel news-hub-panel--npm${projectExpanded ? " is-hidden" : ""}`}>
                            <div className="news-hub-panel-label">
                              NPM DOWNLOADS
                            </div>
                            <div className="news-hub-npm-count">
                              — {npmTotal.toLocaleString()} pobrań
                            </div>
                            {linkedProject && (
                              <div className="news-hub-npm-pkg">
                                {linkedProject.title?.toUpperCase()}
                              </div>
                            )}
                            <p className="news-hub-npm-desc">
                              Osiągnięcie pobrań biblioteki komponentów w ciągu
                              ostatniego miesiąca
                            </p>
                            <div className="news-hub-progress-label">
                              {npmTotal.toLocaleString()} pobrań ({npmPct}%)
                            </div>
                            <div className="news-hub-progress-bar">
                              <div
                                className="news-hub-progress-fill"
                                style={{ width: `${npmPct}%` }}
                              />
                            </div>
                            <div className="news-hub-progress-meta">
                              <span>
                                {npmTotal.toLocaleString()} /{" "}
                                {npmTarget.toLocaleString()}
                              </span>
                              <span>{npmPct}%</span>
                            </div>
                            <div className="news-hub-trend-label">TREND</div>
                            <div className="news-hub-trend">
                              {weeklyTotals.map((w, i) => (
                                <div
                                  key={i}
                                  className={`news-hub-trend-bar${i === weeklyTotals.length - 1 ? " active" : ""}`}
                                  style={{
                                    height: `${Math.max(4, Math.round((w / maxWeek) * 60))}px`,
                                  }}
                                />
                              ))}
                            </div>
                            <div className="news-hub-panel-brand">
                              TEHAQUE.DEV // {new Date().getFullYear()}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </section>
                );
              })()}
            <div className="news-grid">
              {latestNews.map((item) => (
                <div key={item.id} className={`news-card${item.img ? " has-img" : ""}`}>
                  {item.img && (
                    <div className="news-card__img">
                      <img src={`/images/${item.img}`} alt={item.title} />
                    </div>
                  )}
                  <div className="news-card__meta">
                    <span className="news-card__date">{item.date}</span>
                    {item.category?.slice(0, 1).map((c) => (
                      <span key={c} className="tag">
                        {c}
                      </span>
                    ))}
                  </div>
                  <div className="news-card__title">{item.title}</div>
                  <p className="news-card__content">{item.content}</p>
                  <div className="news-card__tags">
                    {item.tags?.map((t) => (
                      <span key={t} className="tag">
                        {t}
                      </span>
                    ))}
                  </div>
                  <div className="news-card__footer">
                    <span className="news-card__author">{author.nickname}</span>
                    <Link to="/news" className="btn btn--sm btn--ghost">
                      Read →
                    </Link>
                  </div>
                </div>
              ))}
            </div>
            <div className="sec-actions">
              <Link to="/news" className="btn btn--primary anim-target">
                All News
              </Link>
            </div>
          </div>
        </section>
        {/* Roadmap */}
        <section className="section">
          <div className="container">
            <div className="sec-header">
              <div className="sec-eyebrow">Roadmap</div>
              <h2 className="sec-title">WHAT'S NEXT</h2>
              <p className="sec-sub">
                Upcoming releases and planned milestones across all active
                projects.
              </p>
            </div>

            <div className="roadmap-list">
              {upcomingRoadmap.map((rm) => (
                <div key={rm.id} className="roadmap-item">
                  <div className="roadmap-item__month">{rm.month}</div>
                  <div className="roadmap-item__goals">
                    {rm.goals.map((g, i) => (
                      <div
                        key={i}
                        className={`roadmap-goal${g.status === "in-progress" ? " roadmap-goal--inprogress" : ""}`}
                      >
                        <div className="roadmap-goal__dot" />
                        <div>
                          <div className="roadmap-goal__title">{g.title}</div>
                          <div className="roadmap-goal__desc">{g.desc}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
        {/* About */}
        <div className="about-strip">
          <div className="about-strip__inner">
            <div>
              <div className="about-strip__avatar">TQ</div>
              <div className="about-strip__name">TEHAQUE</div>
              <p className="about-strip__bio">
                Independent developer and creator. I build games with Unity and
                C#, ship open-source React libraries to NPM, model 3D assets in
                Blender and architect full-stack web products. Everything on
                this site was built solo — from concept to deployment.
              </p>
              <div className="about-strip__specialties">
                {author.specialties?.map((s) => (
                  <span key={s} className="tag">
                    {s}
                  </span>
                ))}
              </div>
            </div>
            <div>
              <div className="sec-eyebrow" style={{ marginBottom: 28 }}>
                Connect
              </div>
              <div className="about-strip__social">
                {author.socialMedia?.github && (
                  <a
                    href={author.socialMedia.github}
                    target="_blank"
                    rel="noreferrer"
                    className="social-btn"
                  >
                    GitHub
                  </a>
                )}
                {author.socialMedia?.twitter && (
                  <a
                    href={author.socialMedia.twitter}
                    target="_blank"
                    rel="noreferrer"
                    className="social-btn"
                  >
                    Twitter
                  </a>
                )}
                {author.socialMedia?.youtube && (
                  <a
                    href={author.socialMedia.youtube}
                    target="_blank"
                    rel="noreferrer"
                    className="social-btn"
                  >
                    YouTube
                  </a>
                )}
                {author.socialMedia?.instagram && (
                  <a
                    href={author.socialMedia.instagram}
                    target="_blank"
                    rel="noreferrer"
                    className="social-btn"
                  >
                    Instagram
                  </a>
                )}
                {author.socialMedia?.linkedin && (
                  <a
                    href={author.socialMedia.linkedin}
                    target="_blank"
                    rel="noreferrer"
                    className="social-btn"
                  >
                    LinkedIn
                  </a>
                )}
                {author.socialMedia?.discord && (
                  <span className="social-btn">
                    Discord: {author.socialMedia.discord}
                  </span>
                )}
              </div>

              {Object.keys(achievements).length > 0 && (
                <div style={{ marginTop: 40 }}>
                  <div className="sec-eyebrow" style={{ marginBottom: 20 }}>
                    Milestones
                  </div>
                  {Object.values(achievements).map((ach) => {
                    const next = ach.milestones?.find((m) => !m.achieved);
                    const progress = next
                      ? Math.min((ach.current / next.value) * 100, 100)
                      : 100;
                    return (
                      <div key={ach.title} style={{ marginBottom: 20 }}>
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            marginBottom: 6,
                          }}
                        >
                          <span
                            style={{
                              fontSize: 12,
                              fontWeight: 600,
                              letterSpacing: "1.5px",
                              textTransform: "uppercase",
                              color: "var(--white2)",
                            }}
                          >
                            {ach.title}
                          </span>
                          <span style={{ fontSize: 12, color: "var(--muted)" }}>
                            {ach.current?.toLocaleString()} /{" "}
                            {next ? next.value.toLocaleString() : "✓"}
                          </span>
                        </div>
                        <div
                          style={{
                            height: 2,
                            background: "var(--border2)",
                            position: "relative",
                          }}
                        >
                          <div
                            style={{
                              height: "100%",
                              width: `${progress}%`,
                              background: "var(--accent)",
                              transition: "width 0.6s ease",
                            }}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Home;
